import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { api } from '../lib/api';
import { Card, Button, Input, Field, ErrorBanner, PageHeader } from '../components/ui';

type BusinessType = 'coach' | 'stylist' | 'realtor' | 'trainer' | 'photographer' | 'other';

const presets: Record<BusinessType, { services: { name: string; durationMinutes: number; priceCents: number }[]; pipelineStages: string[] }> = {
  coach: {
    services: [
      { name: '1-on-1 Coaching', durationMinutes: 60, priceCents: 15000 },
      { name: 'Discovery Call', durationMinutes: 30, priceCents: 0 },
    ],
    pipelineStages: ['New', 'Discovery', 'Proposal', 'Closed'],
  },
  stylist: {
    services: [
      { name: 'Cut', durationMinutes: 45, priceCents: 6000 },
      { name: 'Color', durationMinutes: 90, priceCents: 12000 },
    ],
    pipelineStages: ['Inquiry', 'Booked', 'Returning'],
  },
  realtor: {
    services: [
      { name: 'Property Tour', durationMinutes: 60, priceCents: 0 },
      { name: 'Listing Consultation', durationMinutes: 45, priceCents: 0 },
    ],
    pipelineStages: ['Lead', 'Showings', 'Offer', 'Closed'],
  },
  trainer: {
    services: [
      { name: 'Personal Training', durationMinutes: 60, priceCents: 8000 },
      { name: 'Free Assessment', durationMinutes: 30, priceCents: 0 },
    ],
    pipelineStages: ['Lead', 'Trial', 'Member'],
  },
  photographer: {
    services: [
      { name: 'Portrait Session', durationMinutes: 90, priceCents: 30000 },
      { name: 'Engagement Shoot', durationMinutes: 120, priceCents: 50000 },
    ],
    pipelineStages: ['Inquiry', 'Booked', 'Delivered'],
  },
  other: {
    services: [{ name: 'Consultation', durationMinutes: 60, priceCents: 10000 }],
    pipelineStages: ['New', 'Active', 'Closed'],
  },
};

export function OnboardingPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [businessType, setBusinessType] = useState<BusinessType>('coach');
  const [businessName, setBusinessName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const finishMut = useMutation({
    mutationFn: async () => {
      const preset = presets[businessType];
      // Create services
      await Promise.all(
        preset.services.map((s) =>
          api.post('/services', { ...s, active: true, requiresPayment: false, depositPercent: 0, maxBookingsPerSlot: 1 }).catch(() => null)
        )
      );
      // Create default pipeline + stages
      try {
        const pipeline = await api.post<{ id: string }>('/pipelines', { name: 'Sales', isDefault: true });
        await Promise.all(
          preset.pipelineStages.map((name, idx) =>
            api.post(`/pipelines/${pipeline.id}/stages`, { name, position: idx, color: '#3b82f6' }).catch(() => null)
          )
        );
      } catch {
        /* ignore */
      }
    },
    onSuccess: () => navigate('/app'),
    onError: (e) => setError(e instanceof Error ? e.message : 'Setup failed'),
  });

  const types: { value: BusinessType; label: string; emoji: string }[] = [
    { value: 'coach', label: 'Coach', emoji: '🏆' },
    { value: 'stylist', label: 'Stylist / Salon', emoji: '✂️' },
    { value: 'realtor', label: 'Realtor', emoji: '🏠' },
    { value: 'trainer', label: 'Personal trainer', emoji: '💪' },
    { value: 'photographer', label: 'Photographer', emoji: '📷' },
    { value: 'other', label: 'Other', emoji: '✨' },
  ];

  return (
    <div style={{ minHeight: '100vh', background: 'var(--color-surface-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 'var(--space-4)' }}>
      <Card style={{ maxWidth: 560, width: '100%' }}>
        <PageHeader title={step === 0 ? 'Welcome' : 'Tell us about your business'} subtitle={`Step ${step + 1} of 2`} />

        {step === 0 && (
          <div>
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', marginBottom: 'var(--space-3)' }}>
              Pick your business type and we'll set up sensible defaults.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 'var(--space-2)' }}>
              {types.map((t) => (
                <button
                  key={t.value}
                  onClick={() => setBusinessType(t.value)}
                  style={{
                    padding: 'var(--space-3)',
                    border: businessType === t.value ? '2px solid var(--color-brand-primary)' : '1px solid var(--color-border-primary)',
                    borderRadius: 'var(--radius-md)',
                    background: 'var(--color-surface-primary)',
                    cursor: 'pointer',
                    fontSize: 'var(--text-sm)',
                    textAlign: 'left',
                  }}
                >
                  <div style={{ fontSize: 24, marginBottom: 4 }}>{t.emoji}</div>
                  {t.label}
                </button>
              ))}
            </div>
            <div style={{ marginTop: 'var(--space-4)', display: 'flex', justifyContent: 'flex-end' }}>
              <Button onClick={() => setStep(1)}>Next</Button>
            </div>
          </div>
        )}

        {step === 1 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
            <Field label="Business name">
              <Input value={businessName} onChange={setBusinessName} placeholder="Your business name" autoFocus />
            </Field>
            <div style={{ background: 'var(--color-surface-secondary)', padding: 'var(--space-3)', borderRadius: 'var(--radius-md)', fontSize: 'var(--text-sm)' }}>
              <strong>We'll set up:</strong>
              <ul style={{ margin: 'var(--space-1) 0 0', paddingLeft: 'var(--space-4)' }}>
                {presets[businessType].services.map((s) => (
                  <li key={s.name}>
                    {s.name} ({s.durationMinutes} min)
                  </li>
                ))}
                <li>Pipeline with stages: {presets[businessType].pipelineStages.join(' > ')}</li>
              </ul>
            </div>
            <ErrorBanner error={error} />
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Button variant="ghost" onClick={() => setStep(0)}>
                Back
              </Button>
              <Button onClick={() => finishMut.mutate()} disabled={finishMut.isPending}>
                {finishMut.isPending ? 'Setting up…' : 'Finish setup'}
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
